import { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getAuditLog } from '../services/audit_service'
import './ManagerInsights.css'
import './AuditLog.css'

const PAGE_SIZE = 25
const emptyFilters = {
  user: '',
  entity: '',
  method: '',
  fromDate: '',
  toDate: '',
}
const ENTITIES = [
  'orders',
  'customers',
  'samples',
  'books',
  'stocks',
  'print-requests',
  'payments',
  'old-payments',
  'royalties',
  'authors',
  'users',
  'analytics',
  'payment_reminder',
]
const METHOD_LABELS = {
  POST: 'Created',
  PUT: 'Updated',
  PATCH: 'Updated',
  DELETE: 'Deleted',
  SYSTEM: 'System',
}

const formatDateTime = (value) => {
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
}
const formatDetails = (value) => {
  if (!value) return ''
  try {
    return JSON.stringify(JSON.parse(value), null, 2)
  } catch {
    return value
  }
}

function AuditLog() {
  const [filters, setFilters] = useState(emptyFilters)
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters)
  const [page, setPage] = useState(1)
  const [result, setResult] = useState({ entries: [], total: 0 })
  const [expandedId, setExpandedId] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const controller = new AbortController()
    setIsLoading(true)
    setError('')
    getAuditLog(
      { ...appliedFilters, page, limit: PAGE_SIZE },
      controller.signal,
    )
      .then(setResult)
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message)
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false)
      })
    return () => controller.abort()
  }, [appliedFilters, page])

  const updateFilter = (event) => {
    const { name, value } = event.target
    setFilters((current) => ({ ...current, [name]: value }))
  }
  const applyFilters = (event) => {
    event.preventDefault()
    setPage(1)
    setAppliedFilters(filters)
  }
  const clearFilters = () => {
    setFilters(emptyFilters)
    setPage(1)
    setAppliedFilters(emptyFilters)
  }

  const totalPages = Math.max(Math.ceil(result.total / PAGE_SIZE), 1)

  return (
    <div className='insights-page'>
      <nav className='insights-page__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span aria-hidden='true'>/</span>
        <span>Admin</span>
        <span aria-hidden='true'>/</span>
        <strong>Audit log</strong>
      </nav>
      <header className='insights-page__header'>
        <div>
          <span className='app-content__eyebrow'>Accountability</span>
          <h1>Audit log</h1>
          <p>
            Every create, update, approval and delete, with who made it and
            when. Passwords and tokens are never recorded.
          </p>
        </div>
      </header>

      <form className='insights-filters' onSubmit={applyFilters}>
        <label>
          User
          <input
            name='user'
            value={filters.user}
            onChange={updateFilter}
            placeholder='Name contains'
          />
        </label>
        <label>
          Area
          <select name='entity' value={filters.entity} onChange={updateFilter}>
            <option value=''>All areas</option>
            {ENTITIES.map((entity) => (
              <option key={entity} value={entity}>
                {entity}
              </option>
            ))}
          </select>
        </label>
        <label>
          Action
          <select name='method' value={filters.method} onChange={updateFilter}>
            <option value=''>All actions</option>
            <option value='POST'>Created</option>
            <option value='PUT'>Updated (PUT)</option>
            <option value='PATCH'>Updated (PATCH)</option>
            <option value='DELETE'>Deleted</option>
            <option value='SYSTEM'>System</option>
          </select>
        </label>
        <label>
          From
          <input
            type='date'
            name='fromDate'
            value={filters.fromDate}
            max={filters.toDate || undefined}
            onChange={updateFilter}
          />
        </label>
        <label>
          To
          <input
            type='date'
            name='toDate'
            value={filters.toDate}
            min={filters.fromDate || undefined}
            onChange={updateFilter}
          />
        </label>
        <div className='insights-filters__actions'>
          <button
            className='insights-button insights-button--primary'
            type='submit'
          >
            Search
          </button>
          <button
            className='insights-button'
            type='button'
            onClick={clearFilters}
          >
            Clear
          </button>
        </div>
      </form>

      {error && (
        <div className='insights-page__error' role='alert'>
          {error}
        </div>
      )}

      <section className='insights-card audit-log__card'>
        <header className='insights-card__header'>
          <h2>Activity</h2>
          <p>
            {result.total.toLocaleString()} entries · page {page} of{' '}
            {totalPages}
          </p>
        </header>
        <div className='insights-table-wrap'>
          <table className='insights-table'>
            <thead>
              <tr>
                <th>When</th>
                <th>User</th>
                <th>Action</th>
                <th>Area</th>
                <th>Record</th>
                <th>Path</th>
                <th>
                  <span className='sr-only'>Details</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan='7' className='audit-log__empty'>
                    Loading activity...
                  </td>
                </tr>
              ) : result.entries.length ? (
                result.entries.map((entry) => (
                  <Fragment key={entry.id}>
                    <tr>
                      <td>{formatDateTime(entry.created_at)}</td>
                      <td>{entry.user_name || 'System'}</td>
                      <td>
                        <span
                          className={`audit-log__method audit-log__method--${String(entry.method).toLowerCase()}`}
                        >
                          {METHOD_LABELS[entry.method] || entry.method}
                        </span>
                      </td>
                      <td>{entry.entity}</td>
                      <td>{entry.record_id || '-'}</td>
                      <td className='audit-log__path'>{entry.path}</td>
                      <td>
                        {entry.details && (
                          <button
                            className='insights-button'
                            type='button'
                            onClick={() =>
                              setExpandedId(
                                expandedId === entry.id ? null : entry.id,
                              )
                            }
                            aria-expanded={expandedId === entry.id}
                          >
                            {expandedId === entry.id ? 'Hide' : 'Details'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {expandedId === entry.id && (
                      <tr>
                        <td colSpan='7'>
                          <pre className='audit-log__details'>
                            {formatDetails(entry.details)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan='7' className='audit-log__empty'>
                    No activity matches these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className='audit-log__pager'>
          <button
            className='insights-button'
            type='button'
            onClick={() => setPage((current) => current - 1)}
            disabled={page <= 1 || isLoading}
          >
            Previous
          </button>
          <button
            className='insights-button'
            type='button'
            onClick={() => setPage((current) => current + 1)}
            disabled={page >= totalPages || isLoading}
          >
            Next
          </button>
        </div>
      </section>
    </div>
  )
}

export default AuditLog
